import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Info } from '../../../../icons';
import { Popover, PopoverTrigger, PopoverContent } from '../../../../components/ui/popover';

interface InfoTipProps {
  /** Accessible label for the trigger button (what the icon explains). */
  label: string;
  /** Explanation body. */
  children: ReactNode;
  /** Icon size in px. Defaults to 14. */
  size?: number;
  /** Tailwind width class for the popover. Defaults to `w-64`; widen it for
   *  longer copy so the popover isn't excessively tall. */
  widthClass?: string;
}

export function InfoTip({ label, children, size = 14, widthClass = 'w-64' }: InfoTipProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  useEffect(() => cancelClose, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') {
              cancelClose();
              setOpen(true);
            }
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') scheduleClose();
          }}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex shrink-0 items-center justify-center rounded-full p-1 text-muted-foreground transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info size={size} strokeWidth={2} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={6}
        onPointerEnter={cancelClose}
        onPointerLeave={scheduleClose}
        className={`${widthClass} p-3 text-caption-medium text-muted-foreground`}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
